import { pauseMemory, resumeMemory, startMemory, teardownMemory } from '../../games/memory/memory';
import { openRanking, setRankingApplication } from './ranking';

/**
 * Przebieg rozgrywki po wysłaniu zgłoszenia.
 *
 * Cała gra dzieje się w pełnoekranowym, niezamykalnym modalu (templates/modals/game.html.twig).
 * Ekrany (klasy .modal-screen wewnątrz modalu) pokazywane są pojedynczo:
 *
 *   welcome  -> powitanie: wizytówka konkursu, wejście przyciskiem „Gram"
 *   intro    -> ściągawka przed grą i wybór trybu: trening albo gra o punkty
 *   play     -> plansza (renderuje ją silnik w #memory-game)
 *   result   -> wynik gry próbnej, z wyborem: kolejna próba albo gra o punkty
 *   result-ranked -> wynik gry o punkty, z pozycjami w rankingach
 *   ranking-daily / ranking-global -> pełne klasyfikacje (assets/js/game/ranking.js)
 *   error    -> plansza nieprzygotowana, gra już rozegrana, konkurs nieaktywny
 *   start-expired -> minęło okno na rozpoczęcie gry (bliźniak ekranu błędu)
 *
 * Gier próbnych jest bez ograniczeń i toczą się na planszy próbnej, tasowanej przy każdym
 * podejściu — ćwiczenie nie zdradza więc układu planszy dnia. Do gry o punkty gracz
 * przechodzi wtedy, kiedy sam zdecyduje; przysługuje mu ona raz na dowód zakupu.
 *
 * Modal jest niezamykalny (backdrop static, ESC wyłączony, brak przycisku ×), więc jedyne
 * wyjście prowadzi przez [data-role="finish"] — stąd ten przycisk także na ekranach błędu.
 * KAŻDE takie wyjście przeładowuje stronę: po rozgrywce ma zostać czysty formularz, a nie
 * ekran z danymi poprzedniego zgłoszenia.
 */

const MODAL = '#modal-game';

const KIND_TRIAL = 'trial';
const KIND_RANKED = 'ranked';

let uuid = null;
// Tryb, który wystartuje najbliższe runGame(). Ustawiają go przyciski wyboru gry.
let pendingKind = KIND_TRIAL;
// Gra o punkty jest jedna na dowód zakupu — po niej nie ma już do czego wracać.
let rankedDone = false;
// Ekran, na którym stoi modal — czyta go strażnik opuszczenia strony.
let currentScreen = null;
// Wychodzimy z gry przeładowaniem strony — strażnik opuszczenia ma wtedy milczeć.
let leaving = false;
// Okno na rozpoczęcie gry (Application::GAME_START_WINDOW) — po nim /start odmawia.
let startExpired = false;
let startDeadlineTimer = null;

function $modal() {
    return $(MODAL);
}

function isOpen() {
    return $modal().hasClass('show');
}

function showScreen(name) {
    currentScreen = name;
    $modal().find('.modal-screen').addClass('d-none');
    $modal().find(`.modal-screen[data-screen="${name}"]`).removeClass('d-none');
    $modal().find('.modal-dev-screen').val(name);
}

// ── Okno na rozpoczęcie gry ──────────────────────────────────────────────────

/**
 * Zbroi licznik okna na rozpoczęcie gry.
 *
 * Serwer podaje POZOSTAŁE sekundy, a nie termin — zegar przeglądarki bywa przestawiony,
 * a różnica jest na to odporna. Bez tej wartości (starsza odpowiedź, gra odpalona
 * z pominięciem formularza) okna nie zgadujemy i licznik nie rusza; bramką zostaje
 * wtedy sam /start.
 *
 * @param {number|undefined} secondsLeft pole gameStartExpiresIn z /api/application
 */
function armStartDeadline(secondsLeft) {
    clearStartDeadline();
    startExpired = false;

    if (typeof secondsLeft !== 'number') {
        return;
    }
    if (secondsLeft <= 0) {
        startExpired = true;
        return;
    }

    startDeadlineTimer = window.setTimeout(expireStart, secondsLeft * 1000);
}

function clearStartDeadline() {
    if (startDeadlineTimer !== null) {
        window.clearTimeout(startDeadlineTimer);
        startDeadlineTimer = null;
    }
}

/**
 * Okno minęło.
 *
 * Ekran przejmujemy tylko tam, skąd gracz dopiero szykuje się do gry — na powitaniu
 * i na ściągawce. Trwającej rozgrywki nie przerywamy (serwer też jej nie przerywa, bramka
 * stoi wyłącznie na /start), a wyniku sprzed chwili nie zasłaniamy. Wszędzie indziej
 * wystarczy flaga: złapią ją strażniki w renderIntro i runGame.
 */
function expireStart() {
    startDeadlineTimer = null;
    startExpired = true;

    if (currentScreen === 'welcome' || currentScreen === 'intro') {
        showStartExpired();
    }
}

/** Ślepy zaułek: gra nie ruszy, ale dowód zakupu jest nietknięty i wraca do obiegu. */
function showStartExpired() {
    showScreen('start-expired');
}

/**
 * Ściągawka przed grą — zasady, rankingi i wybór trybu. Treść jest statyczna; ekran
 * decyduje tylko o tym, które wejścia w grę są jeszcze dostępne.
 */
function renderIntro() {
    if (startExpired) {
        return showStartExpired();
    }

    const $intro = $modal().find('.modal-screen[data-screen="intro"]');

    // Trening jest bez ograniczeń, ale potrzebuje planszy próbnej; gra o punkty
    // przysługuje raz na dowód zakupu.
    $intro.find('[data-role="start-trial"]')
        .toggleClass('d-none', !$modal().data('trial-available'));
    $intro.find('[data-role="start-ranked"]').toggleClass('d-none', rankedDone);

    showScreen('intro');
}

/** Uruchamia rozgrywkę wskazaną przez `pendingKind`. */
function runGame() {
    if (startExpired) {
        return showStartExpired();
    }

    const container = document.getElementById('memory-game');
    if (!container || !uuid) return;

    const trial = pendingKind === KIND_TRIAL;

    showScreen('play');

    startMemory(container, {
        uuid,
        kind: trial ? KIND_TRIAL : KIND_RANKED,
        scriptUrl: $modal().data('engine-src') || null,

        // Wynik, błędy i zegar na żywo rysuje HUD silnika (assetsConfig.hud) — musi
        // odświeżać się w tej samej klatce co wypalająca się pula, więc nie dublujemy go tutaj.
        onDone: (score, reason, result) => {
            if (!trial) {
                rankedDone = true;
            }
            renderResult(score, result, trial);
        },

        onError: (error) => showError(error),
    });
}

function renderResult(score, result, wasTrial) {
    if (!wasTrial) {
        return renderRankedResult(score, result);
    }

    const $result = $modal().find('.modal-screen[data-screen="result"]');

    $result.find('[data-role="score"]').text(score);

    // W normalnym przebiegu gra o punkty czeka tu zawsze — ale gdyby była już zużyta,
    // bez wyjścia gracz zostałby w modalu na kolejnych próbach bez końca.
    $result.find('[data-role="start-ranked"]').toggleClass('d-none', rankedDone);
    $result.find('[data-role="finish"]').toggleClass('d-none', !rankedDone);

    showScreen('result');
}

/**
 * Wynik gry o punkty. Gra przysługuje raz na dowód zakupu, więc ekran nie proponuje
 * powtórki — zamiast tego pokazuje, gdzie gracz wylądował w rankingach.
 */
function renderRankedResult(score, result) {
    const $ranked = $modal().find('.modal-screen[data-screen="result-ranked"]');

    $ranked.find('[data-role="score"]').text(score);

    // Pozycje w rankingach liczone są na żywo, więc /memory/result oddaje je dopiero po
    // zapisaniu wyniku — i tylko wtedy, gdy gra w ogóle wpadła do jakiejś klasyfikacji.
    // Bez nich blok „Jesteś obecnie" zostaje schowany, zamiast świecić myślnikami.
    const ranks = result?.ranks ?? null;
    const hasRanks = Boolean(ranks && (ranks.daily || ranks.general));

    $ranked.find('[data-role="ranks"]').toggleClass('d-none', !hasRanks);
    renderRank($ranked.find('[data-role="rank-daily"]'), ranks?.daily);
    renderRank($ranked.find('[data-role="rank-general"]'), ranks?.general);

    showScreen('result-ranked');
}

/**
 * Jedno miejsce w rankingu wraz z nagrodą, jaka na nim przysługuje. Nagroda pojawia się
 * tylko w zasięgu puli — poza nią zostaje sama pozycja.
 */
function renderRank($rank, rank) {
    $rank.toggleClass('d-none', !rank);
    if (!rank) {
        return;
    }

    $rank.find('[data-role="position"]').text(rank.position);
}

/**
 * Ekran błędu. Komunikaty z /start są rozpoznawane po kodzie, żeby gracz wiedział,
 * czy to chwilowa przeszkoda, czy jego podejście jest już zużyte.
 */
function showError(error) {
    const code = error?.data?.error ?? null;

    // Spóźniony start ma własny ekran. Serwer potrafi go zgłosić także wtedy, gdy licznik
    // w przeglądarce nie zdążył wystrzelić — uśpiona karta, przestawiony zegar — więc
    // odpowiedź z /start jest tu rozstrzygająca.
    if (code === 'start_expired') {
        startExpired = true;
        clearStartDeadline();
        return showStartExpired();
    }

    const messages = {
        already_played: 'Na tym dowodzie zakupu rozegrano już grę o punkty. Zgłoś nowy dowód zakupu, aby zagrać ponownie.',
        board_not_ready: 'Plansza na dziś nie jest jeszcze gotowa. Spróbuj ponownie za chwilę.',
        trial_board_not_ready: 'Gra próbna nie jest jeszcze gotowa. Możesz od razu zagrać o punkty.',
        not_configured: 'Gra nie jest jeszcze skonfigurowana. Spróbuj ponownie za chwilę.',
        contest_inactive: 'Konkurs nie jest obecnie aktywny.',
    };

    const $error = $modal().find('.modal-screen[data-screen="error"]');
    $error.find('[data-role="message"]')
        .text(messages[code] ?? 'Nie udało się uruchomić gry. Odśwież stronę i spróbuj ponownie.');

    // Brak planszy próbnej nie może odciąć gracza od gry o punkty — zostawiamy wyjście.
    $error.find('[data-role="to-ranked"]')
        .toggleClass('d-none', code !== 'trial_board_not_ready' || rankedDone);

    showScreen('error');
}

/**
 * Wyjście z gry — jedno dla wszystkich ekranów.
 *
 * Zamiast chować modal przeładowujemy stronę. Zgłoszenie jest już wysłane, a gra rozegrana
 * albo przepadła, więc jedyne, co ma po niej zostać, to czysty formularz — bez podsumowania
 * i bez danych, które gracz przed chwilą podał.
 */
function finish() {
    leaving = true;
    window.location.reload();
}

// ── Wpięcie w stronę ─────────────────────────────────────────────────────────

/**
 * Startuje rozgrywkę dla świeżo wysłanego zgłoszenia.
 *
 * Wywoływane przez handler formularza (js/form/handler/application.js), a nie przez własne
 * podpięcie pod `submit-success` — dzięki temu jest jedno miejsce, które decyduje, co się
 * dzieje po wysłaniu zgłoszenia, i kolejność kroków jest jawna.
 *
 * @param {object} data odpowiedź z /api/application (musi zawierać uuid)
 * @returns {boolean} czy udało się uruchomić grę
 */
export function startGameFlow(data) {
    if (!$modal().length || !data?.uuid) return false;

    uuid = data.uuid;
    rankedDone = false;
    setRankingApplication(uuid);
    armStartDeadline(data.gameStartExpiresIn);
    // Wartość domyślna — tryb wybiera gracz na ściągawce, a „Jeszcze raz" na ekranie
    // wyniku ustawia go samo.
    pendingKind = $modal().data('trial-available') ? KIND_TRIAL : KIND_RANKED;

    // Zapowiedź renderujemy dopiero po „Gram" — powitanie jest statyczne.
    showScreen('welcome');
    $modal().modal('show');

    return true;
}

// Powitanie -> ściągawka z wyborem trybu.
$(document).on('click', `${MODAL} [data-role="to-intro"]`, function () {
    renderIntro();
});

$(document).on('click', `${MODAL} [data-role="show-result"]`, function () {
    showScreen('result-ranked');
});

$(document).on('click', `${MODAL} [data-role="start-trial"]`, function () {
    pendingKind = KIND_TRIAL;
    runGame();
});

$(document).on('click', `${MODAL} [data-role="start-ranked"]`, function () {
    pendingKind = KIND_RANKED;
    runGame();
});

// Kolejna próba — bez zapowiedzi, prosto na planszę.
$(document).on('click', `${MODAL} [data-role="again"]`, function () {
    pendingKind = KIND_TRIAL;
    runGame();
});

// Ekran wyniku gry o punkty -> pełna klasyfikacja. Ekrany rankingu mają już tylko
// wyjście na stronę główną, tak jak sam wynik.
$(document).on('click', `${MODAL} [data-role="show-ranking"]`, function () {
    const type = $(this).data('ranking-type');

    showScreen(`ranking-${type}`);
    openRanking(type);
});

// Powrót z ekranu wyniku lub błędu do ściągawki; tryb wybiera się dopiero na niej.
$(document).on('click', `${MODAL} [data-role="to-ranked"]`, function () {
    renderIntro();
});

// Gra o punkty rozegrana (albo gra w ogóle nie ruszyła) — zwijamy modal i oddajemy
// sterowanie sekcji zgłoszenia, która pokaże podsumowanie.
$(document).on('click', `${MODAL} [data-role="finish"]`, function () {
    finish();
});

// Silnik ubijamy zawsze przy zniknięciu modalu — także wtedy, gdy modal zamknął ktoś
// programowo, z pominięciem przycisku.
$(document).on('hidden.bs.modal', MODAL, function () {
    teardownMemory();
    clearStartDeadline();
    currentScreen = null;
});

// Opuszczenie strony w trakcie rozgrywki gubi wynik, więc pytamy o potwierdzenie.
// Treść komunikatu daje przeglądarka — returnValue jest tylko sygnałem, że ma zapytać.
window.addEventListener('beforeunload', function (event) {
    // Wyłączone w dev, gdzie pytałoby przy każdym przeładowaniu w trakcie pracy nad modalem.
    // Milczy też przy naszym własnym przeładowaniu — to wyjście z gry, a nie jej porzucenie.
    if (leaving || $modal().is('[data-dev]') || !isOpen()) {
        return;
    }
    event.preventDefault();
    event.returnValue = '';
});

// Zamknięcie karty w trakcie gry zostawia sesję otwartą; sprząta ją
// app:abandon-stale-game-sessions, ale silnik i tak wypada ubić po sobie.
// `pagehide` zamiast `unload` — to drugie jest wycofywane i blokuje bfcache.
window.addEventListener('pagehide', function () {
    teardownMemory();
});

// Strażnik orientacji: na niskich ekranach w poziomie modal zakrywa się zaślepką
// „obróć telefon" (CSS-only, patrz .modal-rotate-cover w components/game-modal.scss —
// media query musi się zgadzać z tym poniżej). Kiedy zaślepka stoi nad trwającą grą,
// pauzujemy silnik i wznawiamy go po powrocie do pionu.
const ROTATE_MQ = window.matchMedia('(orientation: landscape) and (max-height: 500px)');

function handleRotateGuard(event) {
    if (!isOpen() || currentScreen !== 'play') {
        return;
    }
    if (event.matches) {
        pauseMemory();
    } else {
        resumeMemory();
    }
}

if (ROTATE_MQ.addEventListener) {
    ROTATE_MQ.addEventListener('change', handleRotateGuard);
} else if (ROTATE_MQ.addListener) {
    ROTATE_MQ.addListener(handleRotateGuard); // Safari < 14
}

// DEV ONLY: skok na wybrany ekran. Wybór ekranu nie wznawia gry, więc silnik ubijamy.
$(document).on('change', `${MODAL} .modal-dev-screen`, function () {
    const screen = $(this).val();
    teardownMemory();

    if (screen === 'intro') {
        return renderIntro();
    }
    if (screen === 'error') {
        return showError(null);
    }
    if (screen === 'result-ranked') {
        return renderRankedResult(0, null);
    }
    if (screen === 'ranking-daily' || screen === 'ranking-global') {
        showScreen(screen);

        return openRanking(screen.replace('ranking-', ''));
    }
    showScreen(screen);
});
